<template>
  <div class="mx-auto w-full max-w-7xl px-4 pb-16 pt-10 md:px-8 md:pt-16">
    <header class="mb-8">
      <p class="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">{{ $t('projectsPage.kicker') }}</p>
      <h1 class="mt-3 text-3xl font-bold text-[var(--text-primary)] md:text-5xl">{{ $t('projectsPage.title') }}</h1>
      <p class="mt-4 max-w-3xl text-[var(--text-muted)]">
        {{ $t('projectsPage.subtitle') }}
      </p>
    </header>

    <section class="space-y-5">
      <article
        v-for="project in projects"
        :key="project.nameKey"
        :id="project.anchorId"
        class="panel overflow-hidden p-6"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 class="text-2xl font-bold text-[var(--text-primary)]">{{ $t(project.nameKey) }}</h2>
          <span class="rounded-full border border-[var(--border)] px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--accent)]">
            {{ $t(project.categoryKey) }}
          </span>
        </div>

        <div class="mt-5 grid gap-4 md:grid-cols-2">
          <div class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
            <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('projectsPage.labels.problem') }}</p>
            <p class="mt-2 text-sm text-[var(--text-secondary)]">{{ $t(project.problemKey) }}</p>
          </div>
          <div class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
            <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('projectsPage.labels.architecture') }}</p>
            <p class="mt-2 text-sm text-[var(--text-secondary)]">{{ $t(project.architectureKey) }}</p>
          </div>
          <div class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
            <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('projectsPage.labels.stack') }}</p>
            <div class="mt-2 flex flex-wrap gap-2">
              <span
                v-for="stackItem in project.stack"
                :key="`${project.nameKey}-${stackItem}`"
                class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--text-secondary)]"
              >
                {{ stackItem }}
              </span>
            </div>
          </div>
          <div class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
            <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('projectsPage.labels.outcome') }}</p>
            <p class="mt-2 text-sm text-[var(--accent)]">{{ $t(project.outcomeKey) }}</p>
          </div>
        </div>

        <div class="mt-4 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
          <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('projectsPage.labels.challenges') }}</p>
          <ul class="mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
            <li v-for="challenge in project.technicalChallenges" :key="challenge" class="flex gap-2">
              <span class="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
              <span>{{ $t(challenge) }}</span>
            </li>
          </ul>
        </div>

        <div class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4">
          <p class="text-sm text-[var(--text-secondary)]"><span class="font-semibold text-[var(--text-primary)]">{{ $t('projectsPage.labels.visuals') }}:</span> {{ $t(project.visualsKey) }}</p>
          <div class="flex flex-wrap gap-2">
            <a
              v-if="project.links.repository"
              :href="project.links.repository"
              target="_blank"
              rel="noopener"
              class="btn-secondary"
            >
              {{ $t('projectsPage.labels.repository') }}
            </a>
            <a
              v-if="project.links.demo"
              :href="project.links.demo"
              target="_blank"
              rel="noopener"
              class="btn-primary"
            >
              {{ $t('projectsPage.labels.demo') }}
            </a>
          </div>
        </div>

        <div v-if="getReferences(project).length" class="mt-3 text-sm text-[var(--text-muted)]">
          <span class="font-semibold text-[var(--text-secondary)]">{{ $t('projectsPage.labels.related') }}:</span>
          <div class="mt-2 flex flex-wrap gap-2">
            <template v-for="ref in getReferences(project)" :key="ref.labelKey">
              <router-link
                v-if="ref.type === 'internal'"
                :to="ref.to"
                class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--accent)] hover:underline"
              >
                {{ $t(ref.labelKey) }}
              </router-link>
              <a
                v-else
                :href="ref.to"
                target="_blank"
                rel="noopener"
                class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--accent)] hover:underline"
              >
                {{ $t(ref.labelKey) }}
              </a>
            </template>
          </div>
        </div>
      </article>
    </section>

    <section class="mt-10 panel p-6">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-[var(--text-primary)]">{{ $t('projectsPage.flappy.title') }}</h2>
          <p class="mt-2 text-sm text-[var(--text-muted)]">{{ $t('projectsPage.flappy.subtitle') }}</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <router-link :to="{ path: '/flappy', query: { from: $route.fullPath } }" class="btn-primary">
            {{ $t('projectsPage.flappy.play') }}
          </router-link>
          <router-link to="/" class="btn-secondary">
            {{ $t('projectsPage.flappy.back') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { PROJECTS_DETAILED } from '@/constants/projectsDetailed';

export default {
  name: 'ProjectsPage',
  data() {
    return {
      projects: PROJECTS_DETAILED,
    };
  },
  methods: {
    getReferences(project) {
      if (project.references && project.references.length) {
        return project.references;
      }
      if (project.reference) {
        return [project.reference];
      }
      return [];
    },
  },
};
</script>
